import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import FieldRenderer, { FieldDefinition } from './fields/FieldRenderer';

interface Package {
  id: number;
  name: string;
  price: number;
  currency: string;
  features: string[];
  popular: boolean;
  country_name: string;
  country_flag: string;
}

interface TemplateStep {
  id: number;
  step_number: number;
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  fields: FieldDefinition[];
}

interface Template {
  id: number;
  name: string;
  name_ar: string;
  description: string;
  description_ar: string;
  steps: TemplateStep[];
}

interface DynamicRequestWizardProps {
  selectedPackage: Package;
}

const DynamicRequestWizard: React.FC<DynamicRequestWizardProps> = ({ selectedPackage }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState<Template | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [templateLoading, setTemplateLoading] = useState(true);
  const [paymentOption, setPaymentOption] = useState<'pay_now' | 'pay_later'>('pay_now');

  useEffect(() => {
    fetchPackageTemplate();
  }, [selectedPackage.id]);

  const fetchPackageTemplate = async () => {
    try {
      setTemplateLoading(true);
      const response = await apiService.getPackageTemplate(selectedPackage.id);

      if (response.success && response.template) {
        setTemplate(response.template);
        // Initialize form data with empty values for all fields
        const initialData: Record<string, any> = {};
        response.template.steps.forEach(step => {
          step.fields.forEach(field => {
            if (field.type === 'checkbox') {
              initialData[field.key] = [];
            } else {
              initialData[field.key] = '';
            }
          });
        });
        setFormData(initialData);
      }
    } catch (error) {
      // Failed to fetch package template
    } finally {
      setTemplateLoading(false);
    }
  };

  const validateCurrentStep = (): boolean => {
    if (!template) return false;

    const currentStepData = template.steps[currentStep];
    if (!currentStepData) return false;

    const newErrors: Record<string, string> = {};

    currentStepData.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.key];

        if (!value || (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')) {
          newErrors[field.key] = t('validation.required');
        }
      }

      // Additional validation based on field type
      const value = formData[field.key];
      if (value && field.validation) {
        if (field.type === 'email' && value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            newErrors[field.key] = t('validation.invalid_email') || 'Invalid email format';
          }
        }

        if (field.type === 'url' && value) {
          try {
            new URL(value);
          } catch {
            newErrors[field.key] = t('validation.invalid_url') || 'Invalid URL format';
          }
        }

        if (field.type === 'file' && value instanceof File) {
          const maxSize = field.validation.max_size_mb || 5;
          if (value.size > maxSize * 1024 * 1024) {
            newErrors[field.key] = `File size must be less than ${maxSize}MB`;
          }
        }

        if (field.validation.minLength && value && value.length < field.validation.minLength) {
          newErrors[field.key] = `Minimum length is ${field.validation.minLength} characters`;
        }

        if (field.validation.maxLength && value && value.length > field.validation.maxLength) {
          newErrors[field.key] = `Maximum length is ${field.validation.maxLength} characters`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));

    // Clear error when user starts typing
    if (errors[fieldKey]) {
      setErrors(prev => ({ ...prev, [fieldKey]: '' }));
    }
  };

  const nextStep = () => {
    if (validateCurrentStep() && template) {
      setCurrentStep(prev => Math.min(prev + 1, template.steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const submitRequest = async () => {
    if (!template) return;

    try {
      setLoading(true);
      setErrors({}); // Clear previous errors

      // Filter out file objects for the initial request
      const templateData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => !(value instanceof File))
      );

      // Simple request structure - let backend handle field extraction
      const requestData = {
        package_id: selectedPackage.id,
        template_id: template.id,
        template_data: templateData
      };

      const response = await apiService.createRequest(requestData);

      if (response && response.request) {
        const requestId = response.request.id;

        // Upload files separately
        const fileFields = Object.entries(formData).filter(([_, value]) => value instanceof File);

        for (const [fieldKey, file] of fileFields) {
          if (file instanceof File) {
            await uploadDocument(requestId, fieldKey, file);
          }
        }

        // Handle payment based on user choice
        if (paymentOption === 'pay_now') {
          // Redirect to payment
          await processPayment(requestId);
        } else {
          // Pay later - redirect to dashboard
          navigate('/dashboard/requests', {
            state: {
              message: 'Request created successfully! You can complete payment later from your requests page.',
              requestNumber: requestId
            }
          });
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async (requestId: number) => {
    try {
      const response = await fetch('https://patrixglobal.com/backend/api/payment/create_checkout_session.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          request_id: requestId,
          locale: i18n.language
        })
      });

      const result = await response.json();

      if (result.success) {
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
      } else {
        setErrors({ payment: result.error || 'Failed to initialize payment' });
      }
    } catch (error) {
      setErrors({ payment: 'Payment processing failed. Please try again.' });
      throw error; // Re-throw to be caught by submitRequest
    }
  };

  const uploadDocument = async (requestId: number, fieldKey: string, file: File) => {
    const formDataUpload = new FormData();
    formDataUpload.append('document', file);
    formDataUpload.append('request_id', requestId.toString());
    formDataUpload.append('document_type', fieldKey);

    const response = await fetch('https://patrixglobal.com/backend/api/requests/upload.php', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: formDataUpload
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }
  };

  if (templateLoading) {
    return <LoadingSpinner />;
  }

  if (!template) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Template Available</h2>
          <p className="text-gray-600 mb-4">
            This package doesn't have a dynamic template configured. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  const currentStepData = template.steps[currentStep];
  const isReviewAndPaymentStep = currentStep === template.steps.length;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {template.steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                ${currentStep > index ? 'bg-green-500 text-white' :
                  currentStep === index ? 'bg-blue-500 text-white' :
                  'bg-gray-200 text-gray-600'}
              `}>
                {currentStep > index ? '✓' : index + 1}
              </div>
              <span className={`ml-2 text-sm ${currentStep >= index ? 'font-medium' : 'text-gray-500'}`}>
                {isArabic ? step.title_ar || step.title : step.title}
              </span>
              {index < template.steps.length - 1 && (
                <div className={`mx-4 flex-1 h-px ${currentStep > index ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}

          {/* Review & Payment Step */}
          <div className="flex items-center">
            <div className={`mx-4 flex-1 h-px ${currentStep > template.steps.length - 1 ? 'bg-green-500' : 'bg-gray-200'}`} />
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
              ${isReviewAndPaymentStep ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}
            `}>
              {template.steps.length + 1}
            </div>
            <span className={`ml-2 text-sm ${isReviewAndPaymentStep ? 'font-medium' : 'text-gray-500'}`}>
              {t('requestWizard.review_payment') || 'Review & Payment'}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Current Step Content */}
        {!isReviewAndPaymentStep && currentStepData && (
          <div>
            <h2 className="text-2xl font-bold mb-2">
              {isArabic ? currentStepData.title_ar || currentStepData.title : currentStepData.title}
            </h2>
            {currentStepData.description && (
              <p className="text-gray-600 mb-6">
                {isArabic ? currentStepData.description_ar || currentStepData.description : currentStepData.description}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentStepData.fields.map((field) => (
                <div
                  key={field.id}
                  className={field.type === 'textarea' ? 'md:col-span-2' : ''}
                >
                  <FieldRenderer
                    field={field}
                    value={formData[field.key]}
                    onChange={handleFieldChange}
                    error={errors[field.key]}
                    disabled={loading}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review & Payment Step */}
        {isReviewAndPaymentStep && (
          <div>
            <h2 className="text-2xl font-bold mb-6">{t('requestWizard.review_payment') || 'Review & Payment'}</h2>

            {/* Package Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">{t('requestWizard.selectedPackage') || 'Selected Package'}</h3>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">{selectedPackage.country_flag}</span>
                    <h4 className="text-lg font-medium">{selectedPackage.name}</h4>
                  </div>
                  <p className="text-gray-600">{selectedPackage.country_name}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    ${selectedPackage.price} {selectedPackage.currency}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Data Summary */}
            <h3 className="text-lg font-semibold mb-4">{t('requestWizard.reviewInformation') || 'Review Information'}</h3>
            {template.steps.map((step, stepIndex) => (
              <div key={step.id} className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-md font-semibold mb-3">
                  {isArabic ? step.title_ar || step.title : step.title}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {step.fields.map((field) => {
                    const value = formData[field.key];
                    const label = isArabic ? field.label_ar || field.label : field.label;

                    if (!value || (Array.isArray(value) && value.length === 0)) return null;

                    return (
                      <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <strong>{label}:</strong> {' '}
                        {Array.isArray(value) ? value.join(', ') :
                         value instanceof File ? value.name :
                         String(value)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Payment Options */}
            <h3 className="text-lg font-semibold mb-4">{t('requestWizard.payment') || 'Payment'}</h3>
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="pay_now"
                    name="paymentOption"
                    value="pay_now"
                    checked={paymentOption === 'pay_now'}
                    onChange={(e) => setPaymentOption(e.target.value as 'pay_now' | 'pay_later')}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="pay_now" className="text-lg font-medium">
                    {t('requestWizard.payNowRecommended') || 'Pay Now (Recommended)'}
                  </label>
                </div>
                <p className="text-gray-600 ml-7">{t('requestWizard.payNowDesc') || 'Complete payment now to start processing your request immediately.'}</p>

                <div className="flex items-center">
                  <input
                    type="radio"
                    id="pay_later"
                    name="paymentOption"
                    value="pay_later"
                    checked={paymentOption === 'pay_later'}
                    onChange={(e) => setPaymentOption(e.target.value as 'pay_now' | 'pay_later')}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label htmlFor="pay_later" className="text-lg font-medium">
                    {t('requestWizard.payLater') || 'Pay Later'}
                  </label>
                </div>
                <p className="text-gray-600 ml-7">{t('requestWizard.payLaterDesc') || 'You can complete payment from your requests page when ready.'}</p>
              </div>
            </div>

            {(errors.payment || errors.submit) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <p className="text-red-700">{errors.payment || errors.submit}</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`px-6 py-2 rounded-md ${
              currentStep === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-gray-500 text-white hover:bg-gray-600'
            }`}
          >
            {t('requestWizard.back')}
          </button>

          {!isReviewAndPaymentStep ? (
            <button
              onClick={nextStep}
              disabled={loading}
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? t('common.loading') : t('requestWizard.continue')}
            </button>
          ) : (
            <button
              onClick={submitRequest}
              disabled={loading}
              className={`px-6 py-2 rounded-md font-medium ${
                paymentOption === 'pay_now'
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              } disabled:opacity-50`}
            >
              {loading
                ? (paymentOption === 'pay_now' ? t('requestWizard.processing') || 'Processing Payment...' : t('requestWizard.creatingRequest') || 'Creating Request...')
                : (paymentOption === 'pay_now' ? t('requestWizard.submitPayNow') || 'Submit & Pay Now' : t('requestWizard.submitPayLater') || 'Submit & Pay Later')
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicRequestWizard;